import { angleFromCos } from "@/lib/tool-kit/linear-algebra/exact-angle";
import {
  divQ,
  type QuadNumber,
  quadFromRational,
  sqrtRational,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  addR,
  divR,
  isZeroR,
  mulR,
  RATIONAL_ZERO,
  type Rational,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Vector arithmetic in two or three dimensions, kept exact.
 *
 * Components are rationals; a magnitude √(x² + y² + z²) is a square root of a rational
 * and is carried as a number in Q(√d), so |(1, 1)| = √2 and the unit vector (1, 1)/√2 is
 * (√2/2, √2/2) rather than 0.7071….
 */

export type Vec = Rational[];

export function dotProduct(u: Vec, v: Vec): Rational {
  return u.reduce((sum, x, i) => addR(sum, mulR(x, v[i])), RATIONAL_ZERO);
}

export function addVec(u: Vec, v: Vec): Vec {
  return u.map((x, i) => addR(x, v[i]));
}

export function subVec(u: Vec, v: Vec): Vec {
  return u.map((x, i) => subR(x, v[i]));
}

export function scaleVec(u: Vec, k: Rational): Vec {
  return u.map((x) => mulR(x, k));
}

export function normSquared(u: Vec): Rational {
  return dotProduct(u, u);
}

export function isZeroVec(u: Vec): boolean {
  return u.every(isZeroR);
}

/** u × v in 3D. */
export function crossProduct(u: Vec, v: Vec): Vec {
  return [
    subR(mulR(u[1], v[2]), mulR(u[2], v[1])),
    subR(mulR(u[2], v[0]), mulR(u[0], v[2])),
    subR(mulR(u[0], v[1]), mulR(u[1], v[0])),
  ];
}

/** The scalar u_x v_y − u_y v_x, the z-component of the 3D cross product of 2D vectors. */
export function cross2D(u: Vec, v: Vec): Rational {
  return subR(mulR(u[0], v[1]), mulR(u[1], v[0]));
}

export function magnitude(u: Vec): QuadNumber {
  return sqrtRational(normSquared(u));
}

export function unitVector(u: Vec): QuadNumber[] | null {
  if (isZeroVec(u)) {
    return null;
  }
  const length = magnitude(u);
  return u.map((x) => divQ(quadFromRational(x), length));
}

export interface AngleResult {
  /** cos θ, exact. */
  cos: QuadNumber;
  radians: number;
  degrees: number;
  /** The exact angle in degrees when cos θ is one of the standard values. */
  exactDegrees: number | null;
}

/** The angle between two non-zero vectors, 0 ≤ θ ≤ π. */
export function angleBetween(u: Vec, v: Vec): AngleResult | null {
  if (isZeroVec(u) || isZeroVec(v)) {
    return null;
  }
  const dot = dotProduct(u, v);
  const product = mulR(normSquared(u), normSquared(v));
  const cos = divQ(quadFromRational(dot), sqrtRational(product));
  // atan2(|u × v|, u·v) stays accurate near 0 and π, where acos loses digits;
  // |u × v|² = |u|²|v|² − (u·v)² in any dimension.
  const sinSquared = subR(product, mulR(dot, dot));
  const radians = Math.atan2(Math.sqrt(toNumberR(sinSquared)), toNumberR(dot));
  return {
    cos,
    radians,
    degrees: (radians * 180) / Math.PI,
    exactDegrees: angleFromCos(cos),
  };
}

export interface Projection {
  /** The vector projection of u onto v. */
  vector: Vec;
  /** The scalar projection (signed length) of u along v. */
  scalar: QuadNumber;
  /** u minus its projection: the part perpendicular to v. */
  rejection: Vec;
  factor: Rational;
}

/** Project u onto a non-zero v. */
export function projectOnto(u: Vec, v: Vec): Projection | null {
  if (isZeroVec(v)) {
    return null;
  }
  const dot = dotProduct(u, v);
  const factor = divR(dot, normSquared(v));
  const vector = scaleVec(v, factor);
  return {
    vector,
    factor,
    scalar: divQ(quadFromRational(dot), magnitude(v)),
    rejection: subVec(u, vector),
  };
}

/* ----------------------------------------------------------- direction */

export interface PolarForm {
  magnitude: QuadNumber;
  /** Angle from the positive x-axis in [0°, 360°), with atan2's quadrant handling. */
  degrees: number;
  radians: number;
  /** Exact degrees when the direction is a multiple of 30° or 45°. */
  exactDegrees: number | null;
  quadrant: 0 | 1 | 2 | 3 | 4;
}

/** Magnitude and direction angle of a 2D vector. */
export function toPolar(x: Rational, y: Rational): PolarForm | null {
  if (isZeroR(x) && isZeroR(y)) {
    return null;
  }
  const xn = toNumberR(x);
  const yn = toNumberR(y);
  let degrees = (Math.atan2(yn, xn) * 180) / Math.PI;
  if (degrees < 0) {
    degrees += 360;
  }
  const length = sqrtRational(normSquared([x, y]));
  const reference = angleFromCos(divQ(quadFromRational(x), length));
  let exact: number | null = null;
  if (reference !== null) {
    exact = yn < 0 ? 360 - reference : reference;
    if (exact === 360) {
      exact = 0;
    }
  }
  let quadrant: PolarForm["quadrant"] = 0;
  if (xn !== 0 && yn !== 0) {
    if (xn > 0) {
      quadrant = yn > 0 ? 1 : 4;
    } else {
      quadrant = yn > 0 ? 2 : 3;
    }
  }
  return {
    magnitude: length,
    degrees,
    radians: (degrees * Math.PI) / 180,
    exactDegrees: exact,
    quadrant,
  };
}

export interface DirectionAngles {
  /** Angles with the x, y and z axes, 0 ≤ angle ≤ 180°. */
  degrees: number[];
  cosines: QuadNumber[];
  exactDegrees: (number | null)[];
}

/** Direction cosines and the angles a 3D vector makes with each axis. */
export function directionAngles(v: Vec): DirectionAngles | null {
  const unit = unitVector(v);
  if (unit === null) {
    return null;
  }
  const length = Math.sqrt(toNumberR(normSquared(v)));
  return {
    cosines: unit,
    degrees: v.map(
      (x) =>
        (Math.acos(Math.max(-1, Math.min(1, toNumberR(x) / length))) * 180) /
        Math.PI
    ),
    exactDegrees: unit.map(angleFromCos),
  };
}
